import { watch } from 'vue';
import { useElementVisibility } from '@vueuse/core';
import { buildEECProduct } from '@/utils/tracking';

export default defineNuxtPlugin((nuxtApp) => {
  let flushTimer = null;

  const flushTempDataLayer = () => {
    if (typeof window === 'undefined') return;

    clearTimeout(flushTimer);

    const flush = () => {
      window.dataLayer = window.dataLayer || [];
      window.tempDataLayer = window.tempDataLayer || [];

      let toFlush = [];
      const toKeep = [];

      for (const e of window.tempDataLayer) {
        if (e._delayedFlush) {
          toKeep.push(e);
          continue;
        }

        toFlush.push(e);
      }

      toFlush = toFlush.map(({ _delayedFlush, ...rest }) => rest);

      if (toFlush.length > 0) {
        window.dataLayer.push(...toFlush);
        window.tempDataLayer = toKeep;
      }
    };

    flushTimer = setTimeout(flush, 100);
  };

  const eventHandler = (data) => {
    return function trackEvent(event = null) {
      window.tempDataLayer = window.tempDataLayer || [];

      if (data.event_name === 'view_item_list' || data.event_name === 'view_promotion') {
        let found = window.tempDataLayer.findLast((item) => item.event === data.event);

        if (!found || found.ecommerce?.items?.length + (Array.isArray(data.payload) ? data.payload.length : 1) > 20) {
          found = {
            event: data.event,
            event_name: data.event_name,
            ecommerce: { items: [] },
            _delayedFlush: true,
          };

          window.tempDataLayer.push(found);
        }

        const items = Array.isArray(data.payload) ? data.payload : [data.payload];
        found.ecommerce.items.push(...items);

        if (found.ecommerce.items.length >= 20) {
          found._delayedFlush = false;
          flushTempDataLayer();
        }
        return;
      }

      if (data.event_name?.includes('select_')) {
        window.tempDataLayer.push({
          event: data.event,
          event_name: data.event_name,
          ecommerce: { items: [data.payload] },
          _delayedFlush: false,
        });
        window.tempDataLayer.forEach((e) => {
          if (e.event_name?.includes('view_')) {
            e._delayedFlush = false;
          }
        });

        flushTempDataLayer();
        return;
      }

      let found = window.tempDataLayer.findLast((item) => item.event === data.event);

      if (!found) {
        found = {
          ...data,
          ecommerce: data.ecommerce || { items: [] },
          _delayedFlush: data.event_name?.includes('view_'),
        };
        window.tempDataLayer.push(found);
      }

      const items = Array.isArray(data.payload) ? data.payload : [data.payload];
      found.ecommerce.items.push(...items);

      if (!data.event_name?.includes('view_')) {
        found._delayedFlush = false;
        flushTempDataLayer();
      }
    };
  };

  const dlPush = {
    eECaddToCart(input, product = null, quantity = 1, imageUrl = null) {
      const getImageUrl = (product) =>
        product?.images?.edges?.[0]?.node?.src ||
        product?.selected?.image?.url ||
        product?.imageUrl ||
        product?.images?.[0]?.src ||
        null;

      const variantsWithProducts = Array.isArray(input)
        ? input.map((entry) => ({
            variant: entry.variant,
            product: entry.product,
            quantity: entry.quantity || 1,
            imageUrl: entry.imageUrl || getImageUrl(entry.product),
          }))
        : [
            {
              variant: input,
              product,
              quantity,
              imageUrl: imageUrl || getImageUrl(product),
            },
          ];

      const items = variantsWithProducts.map(({ variant, product, quantity, imageUrl }) =>
        buildEECProduct(variant, product, quantity, imageUrl)
      );

      if (items.length === 0) return;

      const data = {
        event: 'EEC_add_to_cart',
        event_name: 'add_to_cart',
        currencyCode: variantsWithProducts[0]?.variant?.price?.currencyCode,
        ecommerce: { items },
      };

      nuxtApp.$_gtm.push(data);
    },
  };

  nuxtApp.vueApp.directive('track', {
    mounted(el, binding) {
      if (binding.value === false) return;
      if (!binding.value) {
        console.warn('[TRK]', 'No value provided for v-track directive. Tracking disabled for this element.');
        return;
      }

      let eventType = 'promotion';

      if (binding.arg === 'click') {
        if (binding.modifiers.item) {
          eventType = 'item';
        }

        const eventListener = el.nodeName.toLowerCase() === 'form' ? 'submit' : 'click';

        flushTempDataLayer();
        const handler = eventHandler({
          payload: binding.value,
          event: `EEC_select_${eventType}`,
          event_name: `select_${eventType}`,
        });

        el.addEventListener(eventListener, (event) => {
          handler(event);
        });
      }

      if (binding.arg === 'view') {
        if (binding.modifiers.item) {
          eventType = 'item_list';
        }

        const isInViewport = useElementVisibility(el);
        const payload = binding.value;

        watch(isInViewport, (value) => {
          if (value && !el.dataset.isViewed) {
            el.dataset.isViewed = true;

            const itemsToTrack = Array.isArray(payload) ? payload : [payload];
            const validItems = itemsToTrack.filter((item) => item != null);

            if (validItems.length === 0) return;

            const flattenedItems = validItems.flat();

            const data = {
              event: `EEC_view_${eventType}`,
              event_name: `view_${eventType}`,
              payload: flattenedItems,
            };

            eventHandler(data)();
          }
        });
      }
    },

    unmounted(el, binding) {
      if (binding.arg === 'click') {
        el.removeEventListener('click', eventHandler);
      }
    },
  });

  if (typeof window !== 'undefined') {
    document.addEventListener('visibilitychange', function () {
      if (document.hidden) {
        window.tempDataLayer = window.tempDataLayer || [];
        window.tempDataLayer.forEach((e) => (e._delayedFlush = false));
        flushTempDataLayer();
      }
    });

    window.addEventListener('beforeunload', () => {
      flushTempDataLayer();
    });

    window.addEventListener('OneTrustGroupsUpdated', function (event) {
      const consentSettings = nuxtApp.$getCookieConsent();

      const valueObject = {
        con: { CMP: { a: '', m: '', p: '', s: '' } },
        v: '',
        region: '',
        reg: '',
        purpose: {
          display_banner: false,
          sale_of_data_region: false,
        },
        cus: {
          'onetrust.groups': consentSettings.groups,
          'onetrust.datestamp': new Date().toISOString(),
          'onetrust.dsId': consentSettings.consentId,
        },
      };

      const cookieValue = encodeURIComponent(JSON.stringify(valueObject));
      const cookieName = '_tracking_consent';
      const domain = '.blackroll.com';
      const expires = new Date(Date.now() + 365 * 24 * 60 * 60 * 1000).toUTCString();

      document.cookie = `${cookieName}=${cookieValue}; expires=${expires}; path=/; domain=${domain}; secure; SameSite=None`;
    });
  }

  return {
    provide: {
      flushTempDataLayer,
      dlPush,
    },
  };
});
